import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useScreenWakeLock } from '@libs/hooks/useScreenWakeLock';
import { getSessionStorage } from '@libs/storage';

import { useTournamentVideoSocket } from '@features/shared/api/club/hooks';
import {
  useActiveTournamentParticipantsQuery,
  useTournamentCardQuery,
  useTournamentPrivateVideoTicketMutation,
} from '@features/tournament/api/hooks';
import { parseParticipantAux } from '@features/tournament/types';
import { useCachedTicket } from '@features/video/hooks/useCachedTicket';
import { usePrivateRoomFlag } from '@features/video/hooks/usePrivateRoomFlag';
import { useVideoStream } from '@features/video/hooks/useVideoStream';
import { decodeTicket, isTicketExpired } from '@features/video/lib/ticket';
import { parseToggles } from '@features/video/lib/toggles';
import { RoomStatus, StreamToggles, VideoStreamMode, VideoTicket } from '@features/video/types';

import { VideoStreamPage, VideoStreamPageState } from './VideoStreamPage';

type Props = {
  clubId: string;
  tournamentId: string;
  mode: VideoStreamMode;
  ticket: string;
};

// Трансляция шла в этой вкладке: после перезагрузки входим сами, без «Начать трансляцию»,
// с теми же микрофоном, камерой и звуком судей. Вкладка на одну ссылку, поэтому ключ
// простой; забывает его только отбой
const RESUME_KEY = 'video:stream';

// Страница телефона: билет из ссылки, вход на сайт не нужен. Негодный билет — сразу
// заглушка, сокет и запросы не поднимаем
export function VideoStreamPageWidget({ clubId, tournamentId, mode, ticket }: Props) {
  const claims = useMemo(() => decodeTicket(ticket), [ticket]);
  if (!claims || isTicketExpired(claims)) return <VideoStreamPage mode={mode} state="invalid" />;
  return (
    <Stream
      clubId={clubId}
      tournamentId={tournamentId}
      mode={mode}
      ticket={ticket}
      claims={claims}
    />
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Основная камера говорит и слышит судей, дополнительная по умолчанию молчит и звук
// судей не играет. Турнир и свою запись участника берем без входа, по ссылке
function Stream({ clubId, tournamentId, mode, ticket, claims }: Props & { claims: VideoTicket }) {
  const isMain = mode === 'main';
  const { data: tournament } = useTournamentCardQuery(clubId, tournamentId);
  const participants = useActiveTournamentParticipantsQuery(
    clubId,
    tournamentId,
    'NumberAsc',
    false,
  );
  const me = participants.data?.find((p) => p.userId === claims.sub);
  // Бан в турнире или удаление турнира закрывают видеоконтроль; окончание турнира — нет:
  // билет из ссылки живет сутки и проверен на входе. Пока списки не пришли — верим ссылке
  const isAvailable =
    (!tournament || (!!tournament.videoSettings?.enable && tournament.status !== 'Deleted')) &&
    (!participants.data || me?.status === 'Participating');

  // Флаг приватной комнаты: начало — своя запись, дальше события хаба турнира по билету
  const [inPrivateRoom, applyAux] = usePrivateRoomFlag(
    me ? parseParticipantAux(me.aux).privateVideoRoom === true : undefined,
  );
  useTournamentVideoSocket(tournamentId, ticket, (e) => {
    if (e.eventType === 'ParticipantAuxUpdated' && e.userId === claims.sub) applyAux(e.aux);
  });

  // Приватный билет — групповым билетом как Bearer, кешируем до истечения
  const { mutateAsync: requestPrivateTicket } = useTournamentPrivateVideoTicketMutation(
    clubId,
    tournamentId,
    ticket,
  );
  const { ensureTicket: ensurePrivateTicket } = useCachedTicket(requestPrivateTicket);

  // Билет той комнаты, где камера должна быть сейчас, и флаг, на который камера уже
  // ответила входом или переходом
  const refRoomFlag = useRef(false);
  const roomTicket = useCallback(
    () => (inPrivateRoom ? ensurePrivateTicket() : Promise.resolve(ticket)),
    [inPrivateRoom, ensurePrivateTicket, ticket],
  );

  // Сохраненное при входе в этой вкладке: микрофон, камера и звук судей, иначе — по роли
  const [saved] = useState(readResume);
  const cam = useVideoStream({
    mode,
    clubId,
    tournamentId,
    userId: claims.sub,
    micOn: saved?.micOn ?? isMain,
    camOn: saved?.camOn ?? true,
    speakerOn: saved?.speakerOn ?? isMain,
  });
  const { phase, openSetup, switchRoom, hangUp, join: joinRoom } = cam;

  // Сам повесил трубку: экран «Вы отсоединились», устройства не запрашиваем, пока он не
  // нажмет «Присоединиться снова» — иначе телефон сразу спрашивает разрешения заново
  const [hasLeft, setHasLeft] = useState(false);

  // Превью запрашиваем сразу; после отбоя — только по нажатию
  useEffect(() => {
    if (isAvailable && phase === 'idle' && !hasLeft) openSetup();
  }, [isAvailable, phase, hasLeft, openSetup]);

  // Вход запоминаем на вкладку, отбой забывает: после перезагрузки входим сами. Без клика
  // браузер может не дать звук — тогда плашка попросит нажатие
  const join = useCallback(() => {
    refRoomFlag.current = inPrivateRoom;
    joinRoom(roomTicket);
  }, [inPrivateRoom, joinRoom, roomTicket]);
  // В комнате держим флаг входа с текущими кнопками: нажатия игрока, мьют от судьи
  const { micOn, camOn, speakerOn } = cam;
  const inRoom = isRoomStatus(phase);
  useEffect(() => {
    if (!inRoom) return;
    const toggles: StreamToggles = { micOn, camOn, speakerOn };
    getSessionStorage().setItem(RESUME_KEY, JSON.stringify(toggles));
  }, [inRoom, micOn, camOn, speakerOn]);
  const stop = useCallback(() => {
    getSessionStorage().removeItem(RESUME_KEY);
    setHasLeft(true);
    hangUp();
  }, [hangUp]);
  // Вход снова одним нажатием: устройства запросит сам вход
  const rejoin = useCallback(() => {
    setHasLeft(false);
    join();
  }, [join]);
  const refResumed = useRef(false);
  useEffect(() => {
    if (refResumed.current || !isAvailable || phase !== 'setup') return;
    refResumed.current = true;
    if (saved) join();
  }, [isAvailable, phase, saved, join]);

  // Видеоконтроль закрылся — вешаем трубку
  useEffect(() => {
    if (!isAvailable && isRoomStatus(phase)) hangUp();
  }, [isAvailable, phase, hangUp]);

  // Флаг сменился в эфире — переходим в другую комнату; не перешли — остаемся, без повторов
  useEffect(() => {
    if (phase !== 'live' || refRoomFlag.current === inPrivateRoom) return;
    refRoomFlag.current = inPrivateRoom;
    switchRoom(roomTicket);
  }, [inPrivateRoom, phase, roomTicket, switchRoom]);

  // Потеря связи: вдруг это бан в турнире — перечитаем свою запись
  const { refetch } = participants;
  useEffect(() => {
    if (phase === 'error') refetch();
  }, [phase, refetch]);

  // Экран не гаснет с нажатия «Начать трансляцию» до выхода: скрытая страница теряет камеру
  useScreenWakeLock(isRoomStatus(phase));

  const state: VideoStreamPageState = !isAvailable
    ? 'unavailable'
    : isRoomStatus(phase)
      ? phase
      : phase === 'ended'
        ? 'ended'
        : hasLeft && phase === 'idle'
          ? 'left'
          : 'setup';

  return (
    <VideoStreamPage
      mode={mode}
      state={state}
      tournamentTitle={tournament?.title}
      playerName={claims.name}
      setup={{
        devices: cam.devices,
        error: cam.error,
        onJoin: join,
      }}
      devices={cam.devices}
      meterTrack={cam.meterTrack}
      micOn={cam.micOn}
      camOn={cam.camOn}
      speakerOn={cam.speakerOn}
      onToggleMic={cam.toggleMic}
      onToggleCam={cam.toggleCam}
      onToggleSpeaker={isMain ? undefined : cam.toggleSpeaker}
      onHangUp={stop}
      onRejoin={rejoin}
      micIssue={cam.micIssue}
      audioBlocked={cam.audioBlocked}
      onFixMic={cam.fixMic}
    />
  );
}

// Флаг входа вкладки; битый или чужого формата — как не было входа
function readResume(): StreamToggles | null {
  try {
    const raw = getSessionStorage().getItem(RESUME_KEY);
    return raw ? parseToggles(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function isRoomStatus(phase: string): phase is RoomStatus {
  return (
    phase === 'connecting' || phase === 'waiting' || phase === 'live' || phase === 'reconnecting'
  );
}
